import { Panel } from "primereact/panel"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { useMemo } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { formatFloat, formatNumber } from "@/utils/format"
import { Product_Output, ProductionTask_Input } from "@/api"
import SeparablePanel from "@/components/SeparablePanel"
import { getChartColors } from "./charts/commonChart"

interface MachineSummaryProps {
  selectedMachine: string
  productsById: Record<string, Product_Output>
  productionTasks: ProductionTask_Input[]
  dates: string[]
  holidayDates?: string[]
}

interface DailyItem {
  sku_id: string
  name: string
  quantity: number
}

interface DailyUsageRow {
  date: string
  isHoliday: boolean
  duration: number
  utilization: number
  items: DailyItem[]
}

interface SkuUsageRow {
  sku_id: string
  name: string
  category?: string
  duration: number
  quantity: number
  taskCount: number
  daysProduced: number
}

const MachineSummary = ({
  selectedMachine,
  productsById,
  productionTasks,
  dates,
  holidayDates = [],
}: MachineSummaryProps) => {
  const holidaySet = useMemo(() => new Set(holidayDates), [holidayDates])
  const dayBaseSeconds = 24 * 60 * 60

  const tasksForMachine = useMemo(
    () => productionTasks.filter((t) => t.machine_id === selectedMachine),
    [productionTasks, selectedMachine],
  )

  const dailyUsage = useMemo<DailyUsageRow[]>(() => {
    const map = new Map<string, { duration: number; items: Map<string, DailyItem> }>()
    dates.forEach((d) => {
      map.set(d, { duration: 0, items: new Map<string, DailyItem>() })
    })
    tasksForMachine.forEach((task) => {
      const entry = map.get(task.date) ?? { duration: 0, items: new Map<string, DailyItem>() }
      entry.duration += task.duration ?? 0
      const item = entry.items.get(task.sku_id)
      if (item) {
        item.quantity += task.quantity ?? 0
      } else {
        entry.items.set(task.sku_id, {
          sku_id: task.sku_id,
          name: productsById[task.sku_id]?.name ?? task.sku_id,
          quantity: task.quantity ?? 0,
        })
      }
      map.set(task.date, entry)
    })

    return dates.map((d) => {
      const entry = map.get(d) ?? { duration: 0, items: new Map<string, DailyItem>() }
      return {
        date: d,
        isHoliday: holidaySet.has(d),
        duration: entry.duration,
        utilization: dayBaseSeconds > 0 ? entry.duration / dayBaseSeconds : 0,
        items: Array.from(entry.items.values()).sort((a, b) => a.sku_id.localeCompare(b.sku_id)),
      }
    })
  }, [dates, dayBaseSeconds, holidaySet, productsById, tasksForMachine])

  const workingDays = useMemo(() => dailyUsage.filter((row) => !row.isHoliday), [dailyUsage])
  const activeDays = useMemo(() => dailyUsage.filter((row) => row.duration > 0), [dailyUsage])

  const totalDuration = useMemo(
    () => tasksForMachine.reduce((sum, task) => sum + (task.duration ?? 0), 0),
    [tasksForMachine],
  )
  const totalQuantity = useMemo(
    () => tasksForMachine.reduce((sum, task) => sum + (task.quantity ?? 0), 0),
    [tasksForMachine],
  )

  const averageUtilizationAll = useMemo(() => {
    if (!dailyUsage.length) return 0
    return dailyUsage.reduce((sum, row) => sum + row.utilization, 0) / dailyUsage.length
  }, [dailyUsage])

  const averageUtilizationWorking = useMemo(() => {
    if (!workingDays.length) return 0
    return workingDays.reduce((sum, row) => sum + row.utilization, 0) / workingDays.length
  }, [workingDays])

  const peakUtilization = useMemo(() => {
    return dailyUsage.reduce((max, row) => (row.utilization > max ? row.utilization : max), 0)
  }, [dailyUsage])

  const skuUsage = useMemo<SkuUsageRow[]>(() => {
    const acc = new Map<string, SkuUsageRow & { dates: Set<string> }>()
    tasksForMachine.forEach((task) => {
      const product = productsById[task.sku_id]
      const prev = acc.get(task.sku_id)
      if (prev) {
        prev.duration += task.duration ?? 0
        prev.quantity += task.quantity ?? 0
        prev.taskCount += 1
        prev.dates.add(task.date)
      } else {
        acc.set(task.sku_id, {
          sku_id: task.sku_id,
          name: product?.name ?? task.sku_id,
          category: product?.category,
          duration: task.duration ?? 0,
          quantity: task.quantity ?? 0,
          taskCount: 1,
          daysProduced: 0,
          dates: new Set<string>([task.date]),
        })
      }
    })
    return Array.from(acc.values())
      .map((row) => ({
        sku_id: row.sku_id,
        name: row.name,
        category: row.category,
        duration: row.duration,
        quantity: row.quantity,
        taskCount: row.taskCount,
        daysProduced: row.dates.size,
      }))
      .sort((a, b) => b.duration - a.duration)
  }, [productsById, tasksForMachine])

  const uniqueSkuCount = skuUsage.length
  const uniqueCategoryCount = useMemo(() => {
    const set = new Set<string>()
    skuUsage.forEach((row) => set.add(row.category ?? "未分類"))
    return set.size
  }, [skuUsage])

  const categoryUsage = useMemo(() => {
    const map = new Map<string, number>()
    skuUsage.forEach((row) => {
      const key = row.category ?? "未分類"
      map.set(key, (map.get(key) ?? 0) + row.duration)
    })
    return Array.from(map.entries())
      .map(([category, duration], index) => ({
        name: category,
        value: duration / 3600,
        color: getChartColors(index),
      }))
      .sort((a, b) => b.value - a.value)
  }, [skuUsage])

  const utilizationChartData = useMemo(() => {
    return dailyUsage.map((row) => ({
      date: row.date,
      hours: Number((row.duration / 3600).toFixed(2)),
      utilization: Number((row.utilization * 100).toFixed(1)),
      isHoliday: row.isHoliday,
    }))
  }, [dailyUsage])

  const heatColor = (utilization: number) => {
    const u = Math.max(0, Math.min(1, utilization))
    const hue = 120 - u * 120
    return `hsl(${hue}, 80%, 75%)`
  }

  return (
    <div className="flex flex-column gap-2">
      <h3>{selectedMachine}のサマリ</h3>

      <Panel header="主要指標" toggleable>
        <div className="grid">
          <div className="col-6 md:col-3">
            <div className="text-xs text-600">総成形時間</div>
            <div className="text-xl font-semibold">{formatFloat(totalDuration / 3600, 1)}h</div>
          </div>
          <div className="col-6 md:col-3">
            <div className="text-xs text-600">総生産数</div>
            <div className="text-xl font-semibold">{formatNumber(totalQuantity)}</div>
          </div>
          <div className="col-6 md:col-3">
            <div className="text-xs text-600">生産品目数 / 分類数</div>
            <div className="text-xl font-semibold">{uniqueSkuCount} / {uniqueCategoryCount}</div>
          </div>
          <div className="col-6 md:col-3">
            <div className="text-xs text-600">稼働日数 / 全日数</div>
            <div className="text-xl font-semibold">{activeDays.length} / {dailyUsage.length}</div>
          </div>
          <div className="col-6 md:col-3">
            <div className="text-xs text-600">平均使用率(全日)</div>
            <div className="text-xl font-semibold">{formatFloat(averageUtilizationAll * 100, 1)}%</div>
          </div>
          <div className="col-6 md:col-3">
            <div className="text-xs text-600">平均使用率(稼働日のみ)</div>
            <div className="text-xl font-semibold">{formatFloat(averageUtilizationWorking * 100, 1)}%</div>
          </div>
          <div className="col-6 md:col-3">
            <div className="text-xs text-600">最大使用率</div>
            <div className="text-xl font-semibold">{formatFloat(peakUtilization * 100, 1)}%</div>
          </div>
        </div>
      </Panel>

      <SeparablePanel header="日別 使用率推移" toggleable>
        {utilizationChartData.length === 0 ? (
          <div className="text-center p-4 text-600">対象の日付がありません</div>
        ) : (
          <div className="w-full" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={utilizationChartData} margin={{ top: 5, right: 30, left: 10, bottom: 70 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  interval={0}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(date: string) => {
                    const day = new Date(date).getDay()
                    const dayName = ["日", "月", "火", "水", "木", "金", "土"][day]
                    return `${date.replace(/^\d+-/, "")} (${dayName})`
                  }}
                />
                <YAxis
                  yAxisId="left"
                  label={{ value: "使用時間(h)", angle: -90, position: "insideLeft" }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, 100]}
                  label={{ value: "使用率(%)", angle: 90, position: "insideRight" }}
                />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name === "使用率") return [`${formatFloat(value, 1)}%`, name]
                    return [`${formatFloat(value, 2)}h`, name]
                  }}
                />
                <Legend wrapperStyle={{ fontSize: "0.7rem" }} />
                <Bar yAxisId="left" dataKey="hours" name="使用時間" fill="#1f77b4">
                  {utilizationChartData.map((entry) => (
                    <Cell
                      key={`cell-h-${entry.date}`}
                      fill={entry.isHoliday ? "#cccccc" : "#1f77b4"}
                    />
                  ))}
                </Bar>
                <Bar yAxisId="right" dataKey="utilization" name="使用率" fill="#ff7f0e">
                  {utilizationChartData.map((entry) => (
                    <Cell
                      key={`cell-u-${entry.date}`}
                      fill={entry.isHoliday ? "#dddddd" : heatColor(entry.utilization / 100)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </SeparablePanel>

      <Panel header="日別使用状況" toggleable>
        {dailyUsage.length === 0 ? (
          <div className="text-center p-4 text-600">対象の日付がありません</div>
        ) : (
          <div className="overflow-auto" style={{ maxHeight: 360 }}>
            <table className="w-full text-sm table-auto border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-2 text-left">日付</th>
                  <th className="p-2 text-right">使用時間(h)</th>
                  <th className="p-2 text-right">使用率</th>
                  <th className="p-2 text-left">生産品目 × 生産数</th>
                </tr>
              </thead>
              <tbody>
                {dailyUsage.map((row) => (
                  <tr
                    key={row.date}
                    className={`${row.isHoliday ? "bg-gray-100 text-gray-500" : "odd:bg-white even:bg-gray-50"}`}
                  >
                    <td className="p-2 whitespace-nowrap">
                      {row.date}
                      {row.isHoliday && <span className="ml-1 text-xs">(休)</span>}
                    </td>
                    <td className="p-2 text-right">{formatFloat(row.duration / 3600, 2)}</td>
                    <td
                      className="p-2 text-right"
                      style={{ backgroundColor: row.isHoliday ? undefined : heatColor(row.utilization) }}
                    >
                      {formatFloat(row.utilization * 100, 1)}%
                    </td>
                    <td className="p-2">
                      {row.items.length === 0 ? (
                        <span className="text-gray-400">-</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {row.items.map((item) => (
                            <span
                              key={item.sku_id}
                              className="inline-flex items-center px-1 rounded bg-gray-100 whitespace-nowrap"
                              title={item.sku_id}
                            >
                              {item.name}
                              <span className="ml-1 text-gray-500">× {formatNumber(item.quantity)}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-xs text-gray-500 mt-2">* 24時間を基準に算出。休業日は灰色で表示</div>
          </div>
        )}
      </Panel>

      <Panel header={`生産品目 (${uniqueSkuCount}品目)`} toggleable>
        {skuUsage.length === 0 ? (
          <div className="text-center p-4 text-600">この成形機での生産品目はありません</div>
        ) : (
          <DataTable value={skuUsage} size="small" stripedRows scrollable scrollHeight="360px">
            <Column field="sku_id" header="品目ID" sortable />
            <Column field="name" header="品目名" sortable />
            <Column field="category" header="分類" sortable />
            <Column
              field="quantity"
              header="生産数"
              body={(row: SkuUsageRow) => formatNumber(row.quantity)}
              sortable
            />
            <Column
              field="duration"
              header="成形時間(h)"
              body={(row: SkuUsageRow) => formatFloat(row.duration / 3600, 2)}
              sortable
            />
            <Column field="taskCount" header="タスク数" sortable />
            <Column field="daysProduced" header="生産日数" sortable />
          </DataTable>
        )}
      </Panel>

      <SeparablePanel header="分類別 成形時間分布" toggleable>
        {categoryUsage.length === 0 ? (
          <div className="text-center p-4 text-600">対象の生産タスクがありません</div>
        ) : (
          <div className="w-full" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryUsage}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  labelLine={false}
                  label={(entry) => `${entry.name} ${formatFloat(entry.value, 1)}h`}
                >
                  {categoryUsage.map((entry) => (
                    <Cell key={`cell-${entry.name}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${formatFloat(Number(value), 2)}h`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </SeparablePanel>
    </div>
  )
}

export default MachineSummary
